// ============================================================
// systems/eyeBoss.js — Eye Boss encounter (12000+ clicks)
// ============================================================
// A one-time boss that appears at 12000 clicks.
//   - Screen darkens, camera focuses on eye with "CLICK IT!" text.
//   - Eye slowly moves toward the plushie, looking at it.
//   - Player must click the eye 20-30 times to defeat it.
//   - Click power upgrades DO NOT count — each click is 1 hit.
//   - If it reaches the plushie:
//     - Normal mode: -5000 clicks
//     - Competitive mode: -2 lives
// ============================================================
import { state } from '../state.js';
import { dom } from '../dom.js';
import { applyHazardDamage, loseLife } from './competitive.js';
import { updateShopUI } from './shop.js';

// Movement speed in pixels per frame (~60fps)
const EYE_SPEED = 0.55;
const EYE_SIZE = 140;
const MIN_HITS = 10;
const MAX_HITS = 15;

/**
 * Activate the Eye Boss encounter. Idempotent — returns if already triggered.
 */
export function activateEyeBoss() {
  if (state.eyeBossTriggered || state.eyeBossActive) return;
  state.eyeBossTriggered = true;
  state.eyeBossActive = true;

  // Roll required hits (20–30) and reset hit counter
  state.eyeBossRequiredHits = MIN_HITS + Math.floor(Math.random() * (MAX_HITS - MIN_HITS + 1));
  state.eyeBossHits = 0;

  // Position the eye at a random screen edge
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const side = Math.floor(Math.random() * 4);
  let x, y;
  if (side === 0)      { x = -EYE_SIZE; y = Math.random() * (vh - EYE_SIZE); }       // left
  else if (side === 1) { x = vw; y = Math.random() * (vh - EYE_SIZE); }               // right
  else if (side === 2) { x = Math.random() * (vw - EYE_SIZE); y = -EYE_SIZE; }        // top
  else                 { x = Math.random() * (vw - EYE_SIZE); y = vh; }               // bottom

  state.eyeBossX = x;
  state.eyeBossY = y;

  dom.eyeBossEl.style.left = x + 'px';
  dom.eyeBossEl.style.top = y + 'px';

  // Clear stale classes from any previous encounter
  dom.eyeBossEl.classList.remove('defeated', 'hit');
  dom.eyeBossOverlay.classList.remove('chasing');

  // Rotate the eye to look at the plushie from the start
  updateEyeRotation();

  // Show HP bar with initial fill
  updateHpBar();

  // Show overlay with cinematic entry animation
  dom.eyeBossOverlay.classList.add('show');
  dom.eyeBossEl.classList.add('entering');

  // Camera-shake during entry
  document.body.classList.add('eye-boss-shake');
  setTimeout(() => document.body.classList.remove('eye-boss-shake'), 900);

  // After the entry animation, start moving
  setTimeout(() => {
    if (!state.eyeBossActive) return;
    dom.eyeBossEl.classList.remove('entering');
    dom.eyeBossOverlay.classList.add('chasing');
    state.eyeBossAnimFrame = requestAnimationFrame(updateEyeBoss);
  }, 1400);

  // Click handler is attached ONCE at module init (see bottom of file)

  // Dramatic sound
  playEyeBossEntrySound();
}

function handleEyeClick(e) {
  e.stopPropagation();
  if (!state.eyeBossActive) return;

  // Each click is EXACTLY 1 hit — click power upgrades don't count here
  state.eyeBossHits++;
  updateHpBar();
  playEyeHitSound();

  // Visual feedback: hit flash + shake
  dom.eyeBossEl.classList.remove('hit');
  // Force reflow to restart animation
  void dom.eyeBossEl.offsetWidth;
  dom.eyeBossEl.classList.add('hit');

  if (state.eyeBossHits >= state.eyeBossRequiredHits) {
    defeatEyeBoss();
  }
}

function updateHpBar() {
  const total = state.eyeBossRequiredHits || 1;
  const hits = state.eyeBossHits || 0;
  const pct = Math.max(0, 100 - (hits / total) * 100);
  if (dom.eyeBossHp) dom.eyeBossHp.style.width = pct + '%';
}

/** Compute angle from eye center to plushie and rotate the eye. */
function updateEyeRotation() {
  const rect = dom.smiley.getBoundingClientRect();
  const tx = rect.left + rect.width / 2;
  const ty = rect.top + rect.height / 2;
  const ex = state.eyeBossX + EYE_SIZE / 2;
  const ey = state.eyeBossY + EYE_SIZE / 2;
  const angleDeg = Math.atan2(ty - ey, tx - ex) * (180 / Math.PI);
  dom.eyeBossEl.style.setProperty('--eye-angle', angleDeg + 'deg');
}

function defeatEyeBoss() {
  state.eyeBossActive = false;
  cancelAnimationFrame(state.eyeBossAnimFrame);
  state.eyeBossAnimFrame = null;

  // Explosion effect
  dom.eyeBossEl.classList.add('defeated');
  playEyeBossDefeatSound();

  // Fade out overlay
  setTimeout(() => {
    dom.eyeBossOverlay.classList.remove('show');
    dom.eyeBossOverlay.classList.remove('chasing');
    dom.eyeBossEl.classList.remove('defeated');
    // (click listener stays attached across encounters — it no-ops when inactive)
  }, 500);
}

function hitPlushie() {
  state.eyeBossActive = false;
  cancelAnimationFrame(state.eyeBossAnimFrame);
  state.eyeBossAnimFrame = null;

  // Flash red
  document.body.classList.add('eye-boss-hit');
  setTimeout(() => document.body.classList.remove('eye-boss-hit'), 600);

  // Damage
  if (state.gameMode === 'competitive') {
    loseLife();
    if (state.lives > 0) loseLife(); // second life
  } else {
    applyHazardDamage(5000);
  }
  updateShopUI();

  playEyeBossHitSound();

  // Fade overlay out
  dom.eyeBossEl.classList.add('defeated');
  setTimeout(() => {
    dom.eyeBossOverlay.classList.remove('show');
    dom.eyeBossOverlay.classList.remove('chasing');
    dom.eyeBossEl.classList.remove('defeated');
    // (click listener stays attached across encounters — it no-ops when inactive)
  }, 500);
}

function updateEyeBoss() {
  if (!state.eyeBossActive) return;
  if (state.paused) {
    state.eyeBossAnimFrame = requestAnimationFrame(updateEyeBoss);
    return;
  }

  // Target: center of the plushie
  const rect = dom.smiley.getBoundingClientRect();
  const tx = rect.left + rect.width / 2 - EYE_SIZE / 2;
  const ty = rect.top + rect.height / 2 - EYE_SIZE / 2;

  const dx = tx - state.eyeBossX;
  const dy = ty - state.eyeBossY;
  const dist = Math.sqrt(dx * dx + dy * dy);

  if (dist < 10) {
    hitPlushie();
    return;
  }

  const vx = (dx / dist) * EYE_SPEED;
  const vy = (dy / dist) * EYE_SPEED;
  state.eyeBossX += vx;
  state.eyeBossY += vy;

  dom.eyeBossEl.style.left = state.eyeBossX + 'px';
  dom.eyeBossEl.style.top = state.eyeBossY + 'px';

  // Keep the eye looking at the plushie as it moves
  updateEyeRotation();

  state.eyeBossAnimFrame = requestAnimationFrame(updateEyeBoss);
}

/**
 * Attach the click listener once at module load. To protect against Vite HMR
 * stacking listeners across module reloads, we replace the element with a
 * clone (which strips all old listeners) before binding the current handler.
 */
(function attachClickListenerClean() {
  const old = dom.eyeBossEl;
  const fresh = old.cloneNode(true);
  old.parentNode.replaceChild(fresh, old);
  dom.eyeBossEl = fresh;
  fresh.addEventListener('click', handleEyeClick);
})();

/**
 * Force-stop the eye boss (called on reset/game-over).
 */
export function deactivateEyeBoss() {
  state.eyeBossActive = false;
  state.eyeBossHits = 0;
  if (state.eyeBossAnimFrame) {
    cancelAnimationFrame(state.eyeBossAnimFrame);
    state.eyeBossAnimFrame = null;
  }
  dom.eyeBossOverlay.classList.remove('show');
  dom.eyeBossOverlay.classList.remove('chasing');
  dom.eyeBossEl.classList.remove('entering');
  dom.eyeBossEl.classList.remove('defeated');
  dom.eyeBossEl.classList.remove('hit');
  dom.eyeBossEl.removeEventListener('click', handleEyeClick);
}

/**
 * Reset so the boss can trigger again on a new game.
 */
export function resetEyeBossTrigger() {
  state.eyeBossTriggered = false;
}

// ---- Sound effects ----

function playEyeBossEntrySound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  // Deep bass drone
  const osc = state.audioCtx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(80, now);
  osc.frequency.exponentialRampToValueAtTime(40, now + 1.2);
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0.001, now);
  env.gain.exponentialRampToValueAtTime(0.25, now + 0.3);
  env.gain.exponentialRampToValueAtTime(0.001, now + 1.5);
  osc.connect(env).connect(state.audioCtx.destination);
  osc.start(now); osc.stop(now + 1.6);

  // High shriek
  const osc2 = state.audioCtx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(200, now + 0.2);
  osc2.frequency.linearRampToValueAtTime(1200, now + 0.9);
  const env2 = state.audioCtx.createGain();
  env2.gain.setValueAtTime(0, now);
  env2.gain.linearRampToValueAtTime(0.08, now + 0.4);
  env2.gain.exponentialRampToValueAtTime(0.001, now + 1.2);
  osc2.connect(env2).connect(state.audioCtx.destination);
  osc2.start(now); osc2.stop(now + 1.3);
}

function playEyeBossDefeatSound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  const osc = state.audioCtx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(400, now);
  osc.frequency.exponentialRampToValueAtTime(80, now + 0.4);
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0.2, now);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
  osc.connect(env).connect(state.audioCtx.destination);
  osc.start(now); osc.stop(now + 0.5);
}

function playEyeHitSound() {
  // Short, sharp impact per click
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  const osc = state.audioCtx.createOscillator();
  osc.type = 'square';
  osc.frequency.setValueAtTime(600, now);
  osc.frequency.exponentialRampToValueAtTime(200, now + 0.08);
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0.15, now);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
  osc.connect(env).connect(state.audioCtx.destination);
  osc.start(now); osc.stop(now + 0.15);
}

function playEyeBossHitSound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  const osc = state.audioCtx.createOscillator();
  osc.type = 'square';
  osc.frequency.setValueAtTime(120, now);
  osc.frequency.exponentialRampToValueAtTime(40, now + 0.6);
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0.3, now);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
  osc.connect(env).connect(state.audioCtx.destination);
  osc.start(now); osc.stop(now + 0.7);
}
